import React, { useState, useEffect } from 'react';
import { downloadJSON, downloadCSV, handleFileUpload, getExportStats } from '../services/ExportService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string, type?: 'info' | 'success' | 'error') => void;
}

export const ExportModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const [stats, setStats] = useState({ catches: 0, sessions: 0, areas: 0, totalSize: 0 });
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadStats();
    }
  }, [isOpen]);

  const loadStats = async () => {
    const data = await getExportStats();
    setStats(data);
  };

  if (!isOpen) return null;

  const handleExportJSON = async () => {
    setIsExporting(true);
    try {
      await downloadJSON();
      onSuccess('✅ JSON export klar!', 'success');
    } catch (error) {
      onSuccess('❌ Export misslyckades', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      await downloadCSV();
      onSuccess('✅ CSV export klar!', 'success');
    } catch (error) {
      onSuccess('❌ Export misslyckades', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportFile = () => {
    if (!selectedFile) return;

    setIsImporting(true);
    handleFileUpload(
      selectedFile,
      () => {
        onSuccess('✅ Import lyckades! Laddar om...', 'success');
        setShowImportConfirm(false);
        setSelectedFile(null);
        setIsImporting(false);
        setTimeout(() => window.location.reload(), 1500);
      },
      (error) => {
        onSuccess(`❌ ${error}`, 'error');
        setIsImporting(false);
      }
    );
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setShowImportConfirm(true);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h2>💾 Export & Backup</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.content}>
          {/* Stats */}
          <div style={styles.statsSection}>
            <h3>Din data</h3>
            <div style={styles.statsGrid}>
              <div style={styles.statItem}>
                <div style={styles.statValue}>{stats.catches}</div>
                <div style={styles.statLabel}>Fångster</div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statValue}>{stats.sessions}</div>
                <div style={styles.statLabel}>Sessioner</div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statValue}>{stats.areas}</div>
                <div style={styles.statLabel}>Områden</div>
              </div>
            </div>
          </div>

          {/* Export Section */}
          <div style={styles.section}>
            <h3>📤 Exportera data</h3>
            <p style={styles.description}>
              Spara dina fångster och sessioner som fil.
            </p>
            
            <div style={styles.exportButtons}>
              <button
                onClick={handleExportJSON}
                disabled={isExporting}
                style={styles.exportButton}
              >
                <div style={styles.buttonIcon}>📋</div>
                <div>
                  <div style={styles.buttonTitle}>JSON Backup</div>
                  <div style={styles.buttonDesc}>Komplett backup</div>
                </div>
              </button>

              <button
                onClick={handleExportCSV}
                disabled={isExporting}
                style={styles.exportButton}
              >
                <div style={styles.buttonIcon}>📊</div>
                <div>
                  <div style={styles.buttonTitle}>CSV Export</div>
                  <div style={styles.buttonDesc}>För Excel</div>
                </div>
              </button>
            </div>
          </div>

          {/* Import Section */}
          <div style={styles.section}>
            <h3>📥 Importera data</h3>
            <p style={styles.description}>
              Återställ från tidigare JSON backup.
            </p>
            
            <input
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              style={styles.fileInput}
              id="import-file"
            />
            <label htmlFor="import-file" style={styles.fileLabel}>
              📁 Välj JSON-fil
            </label>

            {selectedFile && (
              <div style={styles.fileInfo}>
                📄 {selectedFile.name}
              </div>
            )}
          </div>

          {/* Warning */}
          <div style={styles.warningBox}>
            ⚠️ Import kommer att ersätta all befintlig data!
          </div>
        </div>

        {/* Import Confirmation */}
        {showImportConfirm && (
          <div style={styles.confirmOverlay}>
            <div style={styles.confirmBox}>
              <h3>⚠️ Bekräfta import</h3>
              <p>Detta kommer att <strong>ersätta</strong> all din nuvarande data med data från filen.</p>
              <p><strong>{selectedFile?.name}</strong></p>
              <div style={styles.confirmActions}>
                <button
                  onClick={() => {
                    setShowImportConfirm(false);
                    setSelectedFile(null);
                  }}
                  style={styles.cancelButton}
                  disabled={isImporting}
                >
                  Avbryt
                </button>
                <button
                  onClick={handleImportFile}
                  style={styles.confirmButton}
                  disabled={isImporting}
                >
                  {isImporting ? 'Importerar...' : 'Importera'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '85vh',
    overflow: 'auto',
    position: 'relative' as const,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
  },
  content: {
    padding: '20px',
  },
  statsSection: {
    marginBottom: '24px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginTop: '12px',
  },
  statItem: {
    backgroundColor: '#f5f5f5',
    padding: '16px',
    borderRadius: '8px',
    textAlign: 'center' as const,
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#007AFF',
  },
  statLabel: {
    fontSize: '12px',
    color: '#666',
    marginTop: '4px',
  },
  section: {
    marginBottom: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #e0e0e0',
  },
  description: {
    color: '#666',
    fontSize: '14px',
    marginBottom: '16px',
  },
  exportButtons: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  exportButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    border: '2px solid #e0e0e0',
    backgroundColor: 'white',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'left' as const,
  },
  buttonIcon: {
    fontSize: '32px',
  },
  buttonTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
  },
  buttonDesc: {
    fontSize: '14px',
    color: '#666',
  },
  fileInput: {
    display: 'none',
  },
  fileLabel: {
    display: 'block',
    padding: '16px',
    border: '2px dashed #007AFF',
    backgroundColor: '#f0f8ff',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'center' as const,
    fontWeight: '500',
    color: '#007AFF',
    transition: 'all 0.2s ease',
  },
  fileInfo: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    fontSize: '14px',
  },
  warningBox: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '8px',
    padding: '12px',
    fontSize: '14px',
    textAlign: 'center' as const,
    fontWeight: '500',
  },
  confirmOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
  },
  confirmBox: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '350px',
    margin: '20px',
  },
  confirmActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    border: '2px solid #e0e0e0',
    backgroundColor: 'white',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  confirmButton: {
    flex: 1,
    padding: '12px',
    border: '2px solid #dc3545',
    backgroundColor: '#dc3545',
    color: 'white',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '500',
  },
};

export default ExportModal;
